import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { useMe, useStreams, useUsage } from '../queries';
import { AccountBadges, Icon, Logo, usd } from '../ui';
import { LegalGate } from './Legal';

function initials(s: string): string {
  const words = s.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
}

export function useSignOut() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    qc.clear();
    navigate({ to: '/login' });
  };
}

export function Shell() {
  const me = useMe();
  const active = me.data?.account.status === 'active';
  const usage = useUsage(1, active);
  const streams = useStreams(active);
  const signOut = useSignOut();

  if (me.isPending) return <div className="center muted">Loading…</div>;
  if (me.error) {
    if (me.error instanceof ApiError && (me.error.status === 401 || me.error.status === 403)) return null; // redirected to /login
    return <div className="center error-text">{me.error.message}</div>;
  }
  const { account, user } = me.data;

  if (me.data.legal && !me.data.legal.accepted) return <LegalGate legal={me.data.legal} accountName={account.name} onSignOut={signOut} />;

  if (account.status !== 'active') {
    return (
      <div className="center">
        <div className="auth" style={{ maxWidth: 460 }}>
          <div className="brand">
            <Logo />
          </div>
          <div className="card">
            {account.status === 'pending' ? (
              <>
                <h1>Thanks, {account.name} is almost ready</h1>
                <p className="muted" style={{ margin: 0 }}>
                  Every new account is reviewed by a person before it can stream. That keeps the platform clean for everyone on it. We'll email <b style={{ color: 'var(--text)' }}>{user.email}</b> as soon as yours is approved.
                </p>
              </>
            ) : (
              <>
                <h1>This account is suspended</h1>
                <p className="muted" style={{ margin: 0 }}>Contact ops@justonair.com if you think this is a mistake.</p>
              </>
            )}
            <button type="button" className="btn" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const live = streams.data?.data.filter((s) => s.status === 'live').length ?? 0;
  const available = usage.data ? Number(usage.data.available_usd) : null;
  const nav = (to: string, icon: string, label: string, extra?: React.ReactNode) => (
    <Link to={to} className="nav" activeProps={{ className: 'nav active' }} activeOptions={{ exact: false }}>
      <Icon name={icon} />
      {label}
      {extra}
    </Link>
  );

  return (
    <div className="app">
      <nav className="side" aria-label="Main">
        <div className="brand">
          <Logo />
        </div>
        <div className="account-chip" title={account.name}>
          <span className="ini">{initials(account.name)}</span>
          <span className="name">{account.name}</span>
          <AccountBadges tier={account.trust_tier} billing={account.billing_mode ?? 'prepaid'} />
        </div>
        {nav('/streams', 'streams', 'Streams', live > 0 ? <span className="count">{live} live</span> : null)}
        {nav('/channels', 'channel', 'Channels')}
        {nav('/usage', 'usage', 'Usage & credit')}
        {nav('/keys', 'key', 'API keys')}
        {nav('/webhooks', 'webhook', 'Webhooks')}
        {nav('/settings', 'settings', 'Settings')}
        <div className="spacer" />
      </nav>
      <div className="main">
        <header className="top">
          {available !== null &&
            ((account.billing_mode ?? 'prepaid') === 'prepaid' ? (
              <Link to="/usage" className={`credit${available < 5 ? ' low' : ''}`}>
                Credit <b>{usd(available)}</b>
              </Link>
            ) : (
              // Postpaid / internal: a negative balance is normal, not a warning.
              <Link to="/usage" className="credit">
                {available < 0 ? (account.billing_mode === 'internal' ? 'Used' : 'Owed') : 'Balance'} <b>{usd(Math.abs(available))}</b>
              </Link>
            ))}
          <a className="muted" href={`mailto:ops@justonair.com?subject=${encodeURIComponent(`Credit for ${account.name}`)}`}>
            Request credit
          </a>
          <Link to="/settings" className="avatar" aria-label="Account settings" title={user.email}>
            {initials(user.name ?? user.email)}
          </Link>
        </header>
        <Outlet />
      </div>
    </div>
  );
}
